import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';

import { apiRequest } from '../utils/apiHelper';
import { formatPrice } from '../utils/formatHelper';
import placeholderImage from '../assets/images/placeholder.jpg';
import '../styles/package.css';
import { showSuccess, showError, showWarning, showInfo, showServerError, dismissAllToasts } from '../utils/toastHelper';

function Package() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    items,
    totalItems,
    totalPrice,
    addToPackage,
    removeFromPackage,
    updateQuantity,
    setSubscriptionType,
    setSubscriptionPlan,
    getStockWarningMessage,
    hasStockWarning,
    resetPackage,
    subscriptionPlan,
    isEditing,
    setIsEditing,
    subscriptionType: contextSubscriptionType
  } = useSubscription();

  const [loading, setLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountResponse, setDiscountResponse] = useState(null);
  const [isCheckingDiscount, setIsCheckingDiscount] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // null | 'ACTIVE' | 'PENDING'
  const [pendingSubscription, setPendingSubscription] = useState(null); // PENDING abonelik detayları
  // Artık local subscriptionType state'i yok, sadece context'teki subscriptionType kullanılacak

  // Global hata yönetimi
  useEffect(() => {
    if (error) {
      console.error('Package page error:', error);
      showError(error.message || 'Beklenmeyen bir hata oluştu. Lütfen sayfayı yenileyin.');
    }

    const handleGlobalError = (event) => {
      event.preventDefault();
      setError(new Error(`JavaScript hatası: ${event.message}`));
      return false;
    };

    window.addEventListener('error', handleGlobalError);

    return () => {
      window.removeEventListener('error', handleGlobalError);
    };
  }, [error]);

  useEffect(() => {
    const checkActiveSubscription = async () => {
      if (!user?.id) return;

      try {
        const response = await apiRequest(`/subscriptions/user/${user.id}`);
        if (response.success && response.subscription) {
          const status = response.subscription.status;

          if (status === 'ACTIVE') {
            if (items.length > 0) {
              if (!isEditing) {
                setIsEditing(true);
              }
            } else {
              setSubscriptionStatus('ACTIVE');
            }
          } else if (status === 'PENDING') {
            if (items.length > 0) {
              if (!isEditing) {
                setIsEditing(true);
              }
            } else {
              setSubscriptionStatus('PENDING');
              setPendingSubscription(response.subscription);
            }
          } else if (status === 'FAILED') {
            showWarning('Abonelik durumunuzda hata tespit edildi. Lütfen aboneliklerim sayfasından kontrol edin.');
            setTimeout(() => {
              navigate(`/subscription/user/${user.id}`);
            }, 3000);
          } else if (status === 'WAITING') {
            showInfo('İptal bekleyen bir aboneliğiniz bulunmaktadır. Aboneliklerim sayfasına yönlendiriliyorsunuz.');
            setTimeout(() => {
              navigate(`/subscription/user/${user.id}`);
            }, 2000);
          }
        } else {
          setSubscriptionStatus(null);
          if (isEditing) {
            setIsEditing(false);
          }
        }
      } catch (error) {
        console.error('Abonelik kontrolü yapılırken bir hata oluştu:', error);
      }
    };

    checkActiveSubscription();
  }, [user?.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Sayfa açıldığında seçim olmamalı
    setSubscriptionType(null);
  }, []);

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromPackage(productId);
      showInfo('Ürün paketten çıkarıldı');
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleSubscriptionTypeChange = (type) => {
    const userKey = `selectedSubscriptionType_${user?.id || 'guest'}`;
    sessionStorage.setItem(userKey, type);
    setSubscriptionType(type);
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Lütfen bir indirim kodu girin');
      return;
    }

    setIsCheckingDiscount(true);
    setDiscountError('');
    setDiscountResponse(null);
    setAppliedDiscount(null);

    try {
      const response = await apiRequest('/subscriptions/validate-discount', {
        method: 'POST',
        body: JSON.stringify({
          code: discountCode,
          total_price: totalPrice
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('Discount API response:', response);

      if (response && response.success && response.discount) {
        try {
          const discount = response.discount;

          const discountValue = discount.value ? parseFloat(discount.value) : 0;
          const discountAmount = discount.discountAmount ? parseFloat(discount.discountAmount) : 0;
          const finalPrice = discount.finalPrice ? parseFloat(discount.finalPrice) : totalPrice;

          let discountPercentage = 0;
          if (discount.type === 'PERCENTAGE') {
            discountPercentage = discountValue;
          } else if (totalPrice > 0) {
            discountPercentage = (discountAmount / totalPrice) * 100;
          }

          if (isNaN(discountPercentage)) {
            discountPercentage = 0;
            console.error('Hesaplanan indirim yüzdesi geçersiz', { discountValue, discountAmount, totalPrice });
          }

          setAppliedDiscount(discountPercentage);
          setDiscountResponse({
            ...response,
            discount: {
              ...discount,
              value: discountValue,
              discountAmount: discountAmount,
              finalPrice: finalPrice
            }
          });

          setDiscountError('');
          showSuccess('İndirim kodu başarıyla uygulandı');
        } catch (calculationError) {
          console.error('İndirim hesaplama hatası:', calculationError);
          setDiscountError('İndirim hesaplanırken bir hata oluştu');
          setAppliedDiscount(null);
          setDiscountResponse(null);
          setError(calculationError);
        }
      } else {
        setDiscountError(response?.message || 'İndirim kodu geçersiz');
        setAppliedDiscount(null);
        setDiscountResponse(null);
      }
    } catch (error) {
      console.error('Discount validation error:', error);
      // 401 hatası için ek toast gösterme (zaten showSessionTimeout çağrıldı)
      if (error.isAuthError) {
        setDiscountError('Oturum süresi doldu. Lütfen tekrar giriş yapın.');
        return;
      }

      let errorMessage = 'İndirim kodu kontrol edilirken bir hata oluştu';

      if (error.apiError) {
        showServerError();
      } else if (error.status === 400) {
        errorMessage = error.message || 'Geçersiz istek';
        setDiscountError(errorMessage);
      } else if (error.status === 404) {
        errorMessage = 'İndirim kodu bulunamadı';
        setDiscountError(errorMessage);
      } else {
        setDiscountError(errorMessage);
        setError(error);
      }

      setAppliedDiscount(null);
      setDiscountResponse(null);
    } finally {
      setIsCheckingDiscount(false);
    }
  };

  const handleContinue = async () => {
    if (items.length === 0) {
      showError('Lütfen paketinize ürün ekleyin');
      return;
    }

    if (!contextSubscriptionType) {
      showError('Lütfen abonelik tipini seçin');
      return;
    }

    setLoading(true);

    try {
      // Aktif abonelik varsa ve düzenleme modunda değilsek, update-products akışına git
      if (isEditing) {
        const activeSubscriptionCheck = await apiRequest(`/subscriptions/user/${user?.id}?is_create=true`);

        if (activeSubscriptionCheck.success && activeSubscriptionCheck.subscription) {
          const subscriptionData = {
            name: contextSubscriptionType === 'monthly' ? 'Aylık Abonelik' : '2 Haftalık Abonelik',
            description: contextSubscriptionType === 'monthly' ? 'Ayda bir teslimat' : '2 haftada bir teslimat',
            price: discountResponse?.discount?.finalPrice || totalPrice,
            interval: contextSubscriptionType === 'monthly' ? 'MONTHLY' : 'BIWEEKLY',
            intervalCount: 1,
            products: items.map(item => ({
              productId: item.id,
              quantity: item.quantity,
              price: item.price,
              title: item.name,
              mainImage: item.mainImage
            })),
            discountCode: appliedDiscount ? discountCode : null
          };

          const response = await apiRequest(`/subscriptions/${user.id}/update-products`, {
            method: 'PUT',
            data: subscriptionData
          });

          if (response.success) {
            if (response.upgradeInfo && response.upgradeInfo.method === "IYZICO_UPGRADE") {
              showSuccess(response.message);
              if (response.upgradeInfo.message) showInfo(response.upgradeInfo.message);
              if (response.changes) {
                const changes = [];
                if (response.changes.productsChanged) changes.push('Ürünler değiştirildi');
                if (response.changes.priceChanged) changes.push('Fiyat değiştirildi');
                if (response.changes.discountChanged) changes.push('İndirim değiştirildi');
                if (changes.length > 0) showInfo(`Değişiklikler: ${changes.join(', ')}`);
              }
              if (response.pendingOrdersHandling?.reason) showInfo(`Not: ${response.pendingOrdersHandling.reason}`);

              setSubscriptionPlan(response.subscription);
              resetPackage();
              setIsEditing(false);
              sessionStorage.removeItem('selectedSubscriptionType_' + user?.id);
              navigate(`/subscription/user/${user?.id}`);
            } else {
              showSuccess(response.message || 'İşlem başarılı');
              setSubscriptionPlan(response.subscription);
              resetPackage();
              setIsEditing(false);
              sessionStorage.removeItem('selectedSubscriptionType_' + user?.id);
              navigate(`/subscription/user/${user?.id}`);
            }
          }
          return; // update-products akışı burada biter
        } else {
          // Editing modunda ama aktif abonelik bulunamadıysa, normal yeni abonelik akışına geç
          setIsEditing(false);
        }
      }

      // YENİ ABONELİK AKIŞI: Backend çağrısı yapılmıyor.
      // Veriler context'e kaydedilip Payment sayfasına yönlendiriliyor.
      // Abonelik kaydı Payment sayfasında ödeme bilgileriyle birlikte oluşturulacak.
      const subscriptionData = {
        name: contextSubscriptionType === 'monthly' ? 'Aylık Abonelik' : '2 Haftalık Abonelik',
        description: contextSubscriptionType === 'monthly' ? 'Ayda bir teslimat' : '2 haftada bir teslimat',
        price: discountResponse?.discount?.finalPrice || totalPrice,
        interval: contextSubscriptionType === 'monthly' ? 'MONTHLY' : 'BIWEEKLY',
        intervalCount: 1,
        products: items.map(item => ({
          productId: item.id,
          quantity: item.quantity,
          price: item.price,
          title: item.name,
          mainImage: item.mainImage
        })),
        discountCode: appliedDiscount ? discountCode : null
      };

      // Context'e plan verilerini kaydet (Payment sayfası bunları kullanacak)
      setSubscriptionPlan(subscriptionData);
      navigate('/subscription/payment');

    } catch (error) {
      console.error('Subscription error:', error);
      if (error.isAuthError) return;

      if (error.data && error.data.details && error.data.actionRequired) {
        if (error.data.actionRequired.type === 'CANCEL_AND_RECREATE') {
          showError(
            <div className="text-start">
              <h6 className="mb-2">{error.data.message}</h6>
              <p className="mb-3">{error.data.details.reason}</p>
              {error.data.actionRequired.steps && error.data.actionRequired.steps.length > 0 && (
                <div className="text-start">
                  <p className="mb-2"><strong>Çözüm:</strong></p>
                  <ul className="mb-0">
                    {error.data.actionRequired.steps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>,
            { autoClose: false, closeOnClick: false, draggable: false, closeButton: true, className: 'error-toast', style: { width: '100%', maxWidth: '600px' } }
          );
        } else {
          showError(error.data.message || 'Abonelik güncellenirken bir hata oluştu');
        }
      } else if (error.data?.recommendation) {
        let errorMessage = error.data.message;
        if (error.data.details?.reason) errorMessage += `\n\nDetay: ${error.data.details.reason}`;
        if (error.data.recommendation) errorMessage += `\n\nÖneri: ${error.data.recommendation}`;
        showError(errorMessage);
      } else if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'İşlem sırasında bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClearPackage = () => {
    if (window.confirm('Paketinizdeki tüm ürünler çıkarılacak. Onaylıyor musunuz?')) {
      dismissAllToasts();
      resetPackage();
      showSuccess('Paketiniz boşaltıldı. Yeni ürünler ekleyebilirsiniz.');
      navigate('/products');
    }
  };

  const handleRemoveProduct = (productId) => {
    removeFromPackage(productId);
    dismissAllToasts();
    showInfo('Ürün paketten çıkarıldı');
  };

  // PENDING aboneliğin ürünlerini sepete yükleyip ödeme sayfasına yönlendirme
  const handleResumePending = () => {
    if (pendingSubscription?.products?.length > 0) {
      pendingSubscription.products.forEach(product => {
        addToPackage({
          id: product.id,
          name: product.name,
          price: product.price,
          quantity: product.quantity || 1,
          mainImage: product.mainImage
        });
      });
      setSubscriptionPlan(pendingSubscription);
      navigate('/subscription/payment');
    } else {
      navigate('/products');
    }
  };

  // PENDING aboneliği iptal edip sıfırdan başlama
  const handleAbandonPending = async () => {
    if (!window.confirm('Tamamlanmamış aboneliğiniz iptal edilecek. Yeni bir abonelik oluşturabilirsiniz. Onaylıyor musunuz?')) return;
    try {
      // Backend'deki PENDING kaydı iptal et
      await apiRequest(`/subscriptions/${user?.id}/cancel-pending`, { method: 'POST' });
      setSubscriptionStatus(null);
      setPendingSubscription(null);
      showSuccess('Tamamlanmamış abonelik iptal edildi. Yeni bir abonelik oluşturabilirsiniz.');
    } catch (error) {
      console.error('PENDING iptal hatası:', error);
      // Hata olsa bile kullanıcıyı engellemeyelim
      setSubscriptionStatus(null);
      setPendingSubscription(null);
    }
  };

  if (items.length === 0) {
    return (
      <div className="package-empty-container">
        <div className="package-empty-content">
          <div className="package-empty-icon">
            <i className="fas fa-shopping-basket"></i>
          </div>
          <h2 className="package-empty-title">Paketiniz Boş</h2>

          {subscriptionStatus === 'ACTIVE' ? (
            <>
              <p className="package-empty-text">
                Aktif bir aboneliğiniz bulunmaktadır.
              </p>
              <div className="package-active-subscription-info">
                <i className="fas fa-check-circle" style={{ color: '#28a745' }}></i>
                <span>Aboneliğiniz aktif ve çalışıyor.</span>
              </div>
              <Link to={`/subscription/user/${user?.id}`} className="package-empty-link">
                <i className="fas fa-arrow-right"></i>
                Aboneliğimi Görüntüle
              </Link>
            </>
          ) : subscriptionStatus === 'PENDING' ? (
            <>
              <p className="package-empty-text">
                Tamamlanmamış bir aboneliğiniz bulunmaktadır. Ödeme adımını tamamlayabilir veya yeni bir abonelik başlatabilirsiniz.
              </p>
              <div className="package-active-subscription-info" style={{ borderColor: '#ffc107', backgroundColor: 'rgba(255, 193, 7, 0.08)' }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#e67e22' }}></i>
                <span>Ödeme bekleniyor.</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem', width: '100%', maxWidth: '320px' }}>
                <button onClick={handleResumePending} className="package-empty-link" style={{ border: 'none', cursor: 'pointer', textAlign: 'center' }}>
                  <i className="fas fa-credit-card"></i>
                  Ödemeye Devam Et
                </button>
                <button
                  onClick={handleAbandonPending}
                  style={{ background: 'none', border: '1px solid #dc3545', borderRadius: '8px', padding: '0.75rem 1.5rem', color: '#dc3545', cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s' }}
                >
                  <i className="fas fa-times" style={{ marginRight: '0.5rem' }}></i>
                  Vazgeç ve Yeniden Başla
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="package-empty-text">
                Abonelik paketinize ürün eklemek için ürünler sayfasına göz atın.
              </p>
              <Link to="/products" className="package-empty-link">
                <i className="fas fa-shopping-basket"></i>
                Ürünlere Göz At
              </Link>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="package-container">
      <div className="package-wrapper">

        {/* Sol Sütun */}
        <div className="package-left-column">

          {/* Hesap */}
          <div className="package-account-section">
            <h2 className="package-section-header">Hesap</h2>
            <p className="package-section-content">{user?.email || 'Misafir'}</p>
          </div>

          {/* Kargo adresi */}
          <div>
            <h2 className="package-section-header">Kargo Adresi</h2>
            <p className="package-section-content">
              {user?.deliveryInfo ?
                `${user.deliveryInfo.contactName}, ${user.deliveryInfo.address}, ${user.deliveryInfo.district}, ${user.deliveryInfo.city}` :
                'Adres bilgisi henüz girilmedi'
              }
            </p>
          </div>

          {/* Kargo yöntemi */}
          <div>
            <h2 className="package-section-header">Kargo Yöntemi</h2>
            <div className="package-shipping-card">
              <span>Ücretsiz Kargo</span>
              <span className="package-shipping-free">ÜCRETSİZ</span>
            </div>
          </div>

          {/* Ödeme */}
          <div>
            <h2 className="package-payment-title">Ödeme</h2>
            <p className="package-payment-subtitle">Tüm işlemler güvenli ve şifrelidir.</p>
            <div className="package-payment-card">
              <div className="package-payment-header">
                <span className="package-payment-provider">iyzico - Kredi ve Banka Kartları</span>
                <div className="package-card-logos">
                  <img src="https://img.icons8.com/color/24/000000/mastercard-logo.png" alt="Mastercard" />
                  <img src="https://img.icons8.com/color/24/000000/visa.png" alt="Visa" />
                  <img src="https://img.icons8.com/color/24/000000/amex.png" alt="Amex" />
                </div>
              </div>
              <div className="package-payment-content">
                <div className="package-payment-placeholder">
                  <div className="package-card-placeholder">
                    <div className="package-card-window">
                      <div className="package-card-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                    <div className="package-card-arrow">
                      <i className="fas fa-arrow-right"></i>
                    </div>
                  </div>
                </div>
                <p className="package-section-content">
                  "Şimdi öde" düğmesine tıkladıktan sonra, satın alımınızı güvenle tamamlamanız için iyzico – Kredi ve Banka Kartları ağ geçidinde yeniden yönlendirilirsiniz.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sağ Sütun */}
        <div className="package-right-column">

          {/* Paketi Boşalt Butonu */}
          {items.length > 0 && (
            <div className="package-clear-container">
              <button
                onClick={handleClearPackage}
                className="package-clear-btn"
                title="Tüm ürünleri paketten çıkar"
              >
                <i className="fas fa-trash-alt"></i>
                Paketi Boşalt
              </button>
            </div>
          )}

          {/* Ürünler */}
          <div className="package-products-container">
            {items.map((item) => (
              <div key={item.id} className="package-product-item">
                <img
                  src={item.mainImage || placeholderImage}
                  alt=""
                  className="package-product-image"
                />
                <div className="package-product-content">
                  <div className="package-product-header">
                    <h3 className="package-product-title">{item.name}</h3>
                    <button
                      onClick={() => handleRemoveProduct(item.id)}
                      className="package-remove-btn"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="package-product-footer">
                    <div className="package-quantity-control">
                      <button
                        onClick={() => handleQuantityChange(item.id, Math.max(1, item.quantity - 1))}
                        disabled={item.quantity <= 1}
                        className="package-qty-btn"
                      >
                        −
                      </button>
                      <span className="package-qty-display">{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        disabled={false}
                        className="package-qty-btn"
                      >
                        +
                      </button>
                    </div>

                    <div className="package-product-price">
                      {item.discountedPrice ? (
                        <div>
                          <div className="package-price-original">
                            {formatPrice(item.price * item.quantity)}
                          </div>
                          <div className="package-price-discounted">
                            {formatPrice(item.discountedPrice * item.quantity)}
                          </div>
                        </div>
                      ) : (
                        <span className="package-price-regular">
                          {formatPrice(item.price * item.quantity)}
                        </span>
                      )}
                      <p className="package-price-unit">
                        <span className="text-muted small me-1">Adet Fiyatı:</span>
                        {formatPrice(item.price)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* İndirim Kodu */}
          <div className="package-discount-container">
            <input
              type="text"
              placeholder="İndirim kodu"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
              className="package-discount-input"
            />
            <button
              onClick={handleApplyDiscount}
              disabled={isCheckingDiscount}
              className="package-discount-btn"
            >
              {isCheckingDiscount ? '...' : 'Uygula'}
            </button>
          </div>

          {discountError && (
            <div className="package-discount-error">{discountError}</div>
          )}

          {appliedDiscount > 0 && discountResponse?.discount && (
            <div className="package-discount-success">
              <div>
                {(() => {
                  try {
                    const discount = discountResponse.discount;
                    let percentageText = "0,00";

                    if (discount.type === 'PERCENTAGE' && discount.value) {
                      percentageText = parseFloat(discount.value).toFixed(2).replace('.', ',');
                    } else if (discount.discountAmount && totalPrice > 0) {
                      percentageText = (parseFloat(discount.discountAmount) / totalPrice * 100).toFixed(2).replace('.', ',');
                    }

                    return `%${percentageText} indirim uygulandı`;
                  } catch (e) {
                    console.error("İndirim yüzdesi hesaplama hatası:", e);
                    return "İndirim uygulandı";
                  }
                })()}
              </div>
              <div className="package-discount-amount">
                İndirimli Tutar: {discountResponse?.discount?.finalPrice
                  ? formatPrice(discountResponse.discount.finalPrice)
                  : formatPrice(totalPrice)}
              </div>
              <div className="package-discount-savings">
                Kazancınız: {discountResponse?.discount?.discountAmount
                  ? formatPrice(discountResponse.discount.discountAmount)
                  : "0"}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger mt-2"
                onClick={() => {
                  setDiscountCode('');
                  setAppliedDiscount(null);
                  setDiscountResponse(null);
                  setDiscountError('');
                }}
              >
                İndirimi Kaldır
              </button>
            </div>
          )}

          {/* Alt Toplam ve Toplam */}
          <div className="package-summary">
            <div className="package-summary-row">
              <span>Alt toplam · {items.reduce((sum, item) => sum + item.quantity, 0)} ürün</span>
              <span>{formatPrice(items.reduce((sum, item) => sum + (item.price * item.quantity), 0))}</span>
            </div>
            {items.some(item => item.discountedPrice) && (
              <div className="package-summary-discount">
                <span>Ürün İndirimleri:</span>
                <span>
                  -{formatPrice(items.reduce((sum, item) => {
                    if (item.discountedPrice) {
                      return sum + ((item.price - item.discountedPrice) * item.quantity);
                    }
                    return sum;
                  }, 0))}
                </span>
              </div>
            )}
            {appliedDiscount > 0 && discountResponse?.discount && (
              <div className="package-summary-discount">
                <span>İndirim:</span>
                <span>
                  -{discountResponse?.discount?.discountAmount ? formatPrice(discountResponse.discount.discountAmount) : '0'}
                </span>
              </div>
            )}
            <div className="package-summary-row">
              <span className="package-shipping-info">
                Kargo <span className="package-help-icon">?</span>
              </span>
              <span className="package-shipping-free-text">ÜCRETSİZ</span>
            </div>
          </div>

          <div className="package-total-section">
            <div className="package-summary-row package-total-row">
              <span className="package-total-label">Toplam</span>
              <span className="package-total-price">
                {formatPrice(
                  appliedDiscount > 0 && discountResponse?.discount?.finalPrice
                    ? discountResponse.discount.finalPrice
                    : items.reduce((sum, item) => {
                      const price = item.discountedPrice || item.price;
                      return sum + (price * item.quantity);
                    }, 0)
                )}
              </span>
            </div>
            <div className="package-tax-info">
              {((appliedDiscount > 0 && discountResponse?.discount?.finalPrice
                ? discountResponse.discount.finalPrice
                : items.reduce((sum, item) => {
                  const price = item.discountedPrice || item.price;
                  return sum + (price * item.quantity);
                }, 0)) * 0.18).toFixed(2).replace('.', ',')} ₺ vergi dahil
            </div>
          </div>

          {/* Abonelik Tipi Seçimi */}
          <div>
            <h3 className="package-subscription-title">Abonelik Tipi</h3>
            <div className="package-subscription-options">
              <div
                onClick={() => handleSubscriptionTypeChange('monthly')}
                className={`package-subscription-option ${contextSubscriptionType === 'monthly' ? 'active' : ''}`}
              >
                <div className="package-subscription-content">
                  <div className="package-subscription-info">
                    <h4>Aylık Abonelik</h4>
                    <p>Ayda bir teslimat</p>
                  </div>
                  <div className={`package-subscription-check ${contextSubscriptionType === 'monthly' ? 'active' : ''}`}>
                    {contextSubscriptionType === 'monthly' && <span>✓</span>}
                  </div>
                </div>
              </div>

              <div
                onClick={() => handleSubscriptionTypeChange('biweekly')}
                className={`package-subscription-option ${contextSubscriptionType === 'biweekly' ? 'active' : ''}`}
              >
                <div className="package-subscription-content">
                  <div className="package-subscription-info">
                    <h4>2 Haftalık Abonelik</h4>
                    <p>2 haftada bir teslimat</p>
                  </div>
                  <div className={`package-subscription-check ${contextSubscriptionType === 'biweekly' ? 'active' : ''}`}>
                    {contextSubscriptionType === 'biweekly' && <span>✓</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleContinue}
            disabled={loading}
            className="package-continue-btn"
          >
            {loading ? (
              <div className="package-loading-content">
                <i className="fas fa-spinner fa-spin package-loading-spinner"></i>
                İşleminiz Yapılıyor...
              </div>
            ) : (
              'Devam Et'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Package; 